"use client";

import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Modal, ModalContent, ModalHeader, ModalBody } from "@heroui/modal";
import { TaskStatus } from "@/types";
import { getMaxPoints } from "@/lib/challenges";
import {
  useGetCampaign,
  useGetCampaignUser,
  useGetCampaignTaskStatus,
  useQuestsMutations,
} from "@/hooks/challenges";
import { CAMPAIGN_ID, CAMPAIGN_GROUP_ID } from "@/config/campaign";
import { useAuth } from "@/hooks/useAuth";
import { QuestLogo } from "@/components";
import { QuestRenderer } from "./tasks/quest-renderer";
import { MarkdownRenderer } from "@/components/markdown-renderer";

interface QuestModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: TaskStatus | null;
}

export const QuestModal = ({ isOpen, onClose, task }: QuestModalProps) => {
  const { address, isAuthenticated: isDynamicAuth } = useAuth();
  const { data: campaign } = useGetCampaign(CAMPAIGN_ID, CAMPAIGN_GROUP_ID);
  const { data: challengesUser } = useGetCampaignUser(
    CAMPAIGN_ID,
    CAMPAIGN_GROUP_ID,
    address || "",
    isDynamicAuth
  );
  const { taskMutation } = useQuestsMutations();

  // Get the latest task status from the fetched data
  const { data: taskStatuses, refetch: refetchTaskStatuses } =
    useGetCampaignTaskStatus(CAMPAIGN_ID, CAMPAIGN_GROUP_ID);

  // Refetch task statuses when modal opens to ensure fresh data
  useEffect(() => {
    if (isOpen && task?.taskGuid) {
      refetchTaskStatuses();
    }
  }, [isOpen, task?.taskGuid, refetchTaskStatuses]);

  // Use the latest task status if available, otherwise use the prop
  const currentTask =
    taskStatuses?.tasksStatus?.find(
      (t: TaskStatus) => t.taskGuid === task?.taskGuid
    ) || task;

  const challengeTask = campaign?.tasks?.find(
    (t) => t.id === currentTask?.taskGuid
  );
  const maxPoints = challengeTask ? getMaxPoints(challengeTask) : 0;

  if (!campaign) return null;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      size="3xl"
      scrollBehavior="inside"
      classNames={{
        backdrop: "bg-black/50 backdrop-blur-sm",
        wrapper: "z-50",
        base: "bg-yiedl-dark text-white max-h-[90vh] rounded-2xl overflow-hidden",
        body: "p-0",
        header: "border-b-0 pb-0 p-0",
        closeButton: "z-50",
      }}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader />
            <ModalBody className="p-0 relative">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={currentTask?.taskGuid}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  {currentTask && (
                    <>
                      {/* Header Banner */}
                      <div className="relative w-full h-48 overflow-hidden">
                        <img
                          src="https://pulsarmoney.b-cdn.net/yiedl/yield-tasks/task-modal-banner.png"
                          alt="Task Banner"
                          className="w-full h-full object-cover"
                        />
                        {/* Points Badge Overlay */}
                        <div className="absolute bottom-3 right-3 bg-black/80 backdrop-blur-sm text-yiedl-primary px-4 py-2 rounded-xl font-bold flex items-center gap-2 shadow-lg">
                          <span className="text-lg">{maxPoints}</span>
                          <QuestLogo size={20} />
                        </div>
                      </div>

                      {/* Task Content */}
                      <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6">
                        {/* Title */}
                        <h2 className="text-xl sm:text-2xl font-bold text-white">
                          {currentTask.title}
                        </h2>

                        {/* Description */}
                        {currentTask.description && (
                          <div className="text-gray-400 text-sm sm:text-base lg:text-lg prose prose-sm sm:prose-base lg:prose-lg prose-gray max-w-none">
                            <MarkdownRenderer>
                              {currentTask.description}
                            </MarkdownRenderer>
                          </div>
                        )}

                        {/* Task Actions */}
                        <div className="relative w-full">
                          <QuestRenderer
                            challengesUser={challengesUser}
                            task={currentTask}
                            address={address || ""}
                            campaign={campaign}
                            completeTaskMutation={taskMutation}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
